import React, { useLayoutEffect, useRef } from 'react'
import Stack from '@mui/material/Stack'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Props } from './types'
import { SX } from './constants'
import { useData } from './hooks/useData'
import { useHandlers } from './hooks/useHandlers'
import { ChipsSection } from './components/ChipsSection'
import { AlertMessage } from './components/AlertMessage'

export function FilterChips({
  hereMap,
  searchDrawerOpen = false,
  filterDrawerOpen = false,
  addLeadDrawerOpen = false,
  onOpenFilterDrawer,
  onMeasure,
}: Props) {
  const isMobile = useMediaQuery('(max-width: 600px)')
  const isTablet = useMediaQuery('(max-width: 1200px)')

  const {
    open,
    setOpen,
    showFilterChips,
    showNoTypesWarning,
    visibleChips,
    extraCount,
    selectedTypes,
    selectedKnockStatusIds,
  } = useData()

  const { handleChipDelete, handleOverflowChipClick } = useHandlers({
    hereMap,
    selectedTypes,
    selectedKnockStatusIds,
    onOpenFilterDrawer,
  })

  const containerRef = useRef<HTMLDivElement>(null)
  const showAlert = showNoTypesWarning && open

  // Report the bottom edge of the chips/alert stack so the loading pill can be
  // placed below it (it can't overlap the chips). Re-measures on content/size
  // changes; reports 0 when the stack isn't rendered.
  useLayoutEffect(() => {
    const el = containerRef.current
    if (!showFilterChips || !el) {
      onMeasure?.(0)
      return
    }
    const report = () => onMeasure?.(el.offsetTop + el.offsetHeight)
    report()
    const observer = new ResizeObserver(report)
    observer.observe(el)
    return () => observer.disconnect()
  }, [showFilterChips, onMeasure, isMobile, isTablet, visibleChips, extraCount])

  if (!showFilterChips) return null

  const isAnyDrawerOpen =
    searchDrawerOpen || filterDrawerOpen || addLeadDrawerOpen

  const visibilitySx = {
    opacity: isAnyDrawerOpen ? 0 : 1,
    visibility: isAnyDrawerOpen ? ('hidden' as const) : ('visible' as const),
    transition: 'opacity 0.5s ease-in-out',
  }

  const chipsNode = (
    <Paper sx={SX.paper}>
      <ChipsSection
        visibleChips={visibleChips}
        extraCount={extraCount}
        onChipDelete={handleChipDelete}
        onOverflowClick={handleOverflowChipClick}
      />
    </Paper>
  )

  const alertNode = showAlert ? (
    <AlertMessage onClose={() => setOpen(false)} />
  ) : null

  // The chips track the top-left of the map and hide while a drawer is open.
  const chipsContainer = isTablet ? (
    <Box
      ref={containerRef}
      sx={{
        position: 'absolute',
        top: isMobile ? 24 : 34,
        left: isMobile ? 16 : 28,
        right: isMobile ? 16 : 'auto',
        zIndex: 50,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.6,
        maxWidth: isMobile ? 'calc(100% - 32px)' : 'auto',
        pointerEvents: 'none',
        ...visibilitySx,
      }}
    >
      {chipsNode}
    </Box>
  ) : (
    <Stack
      ref={containerRef}
      sx={{
        position: 'absolute',
        top: 34,
        left: 28,
        zIndex: 50,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.6,
        justifyContent: 'flex-end',
        pointerEvents: 'none',
        ...visibilitySx,
      }}
    >
      {chipsNode}
    </Stack>
  )

  return (
    <>
      {chipsContainer}
      {/* The "no record types selected" warning is centered in the map and
          stays visible even while the filter drawer is open. */}
      {alertNode && (
        <Box
          sx={{
            position: 'absolute',
            top: 34,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 100,
          }}
        >
          {alertNode}
        </Box>
      )}
    </>
  )
}
